import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../utils/classMerger";

// On-screen script for recording My Niche Video. DOM only: the
// recorder captures camera frames, so this never appears in the video.
const SIZES = { S: "text-base", M: "text-xl", L: "text-3xl" } as const;
type Size = keyof typeof SIZES;

export const DEFAULT_WPM = 150;
export const MIN_WPM = 90;
export const MAX_WPM = 220;
const WPM_STEP = 10;

// Pixels per second that scroll `words` past in words / wpm minutes.
export const scrollPxPerSecond = (scrollable: number, words: number, wpm: number): number =>
    scrollable > 0 && words > 0 && wpm > 0 ? scrollable / ((words / wpm) * 60) : 0;

// 40x40 so a phone or tablet can hit them mid-take; 32x32 from 1024 px, where a laptop's
// portrait frame is ~260 px wide. min-[1024px]:, not lg:, because tailwind.config.js
// keeps only the sxl breakpoint.
const controlClass =
    "inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded bg-white/15 px-3 text-sm hover:bg-white/25 " +
    "min-[1024px]:min-h-[32px] min-[1024px]:min-w-[32px] min-[1024px]:px-2 min-[1024px]:text-xs";

interface TeleprompterProps {
    script: string;
    running: boolean;
}

const Teleprompter: React.FC<TeleprompterProps> = ({ script, running }) => {
    const [wpm, setWpm] = useState(DEFAULT_WPM);
    const [size, setSize] = useState<Size>("M");
    const [paused, setPaused] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const words = script.trim().split(/\s+/).filter(Boolean).length;

    // Every take starts at the top, scrolling; a pause never outlives its take.
    useEffect(() => {
        setPaused(false);
        if (running && scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [running]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!running || paused || !el) return;
        let frame = 0;
        let last = performance.now();
        let pos = el.scrollTop;
        const tick = (now: number): void => {
            const max = el.scrollHeight - el.clientHeight;
            pos = Math.min(pos + (scrollPxPerSecond(max, words, wpm) * (now - last)) / 1000, Math.max(max, 0));
            last = now;
            el.scrollTop = pos;
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [running, paused, wpm, words]);

    const toggle = (): void => {
        if (running) setPaused((p) => !p);
    };

    return (
        // pointer-events-auto: the recorder's frame around this lets taps through; the band still takes them.
        <div data-testid="teleprompter" className="pointer-events-auto absolute inset-x-0 top-0 z-10 flex h-1/3 flex-col bg-black/55 text-white">
            <div
                ref={scrollRef}
                role="button"
                tabIndex={0}
                aria-label={paused ? "Resume teleprompter" : "Pause teleprompter"}
                onClick={toggle}
                onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        toggle();
                    }
                }}
                // leading-snug after the size: tailwind-merge drops a leading-* that a text-* follows.
                className={cn("flex-1 overflow-hidden whitespace-pre-wrap px-4 pt-2", SIZES[size], "leading-snug")}
            >
                {script}
                <div className="h-16" aria-hidden />
            </div>
            {/* Over the script, not in the controls row: the bigger controls leave no room there on a phone. */}
            {paused && (
                <span className="pointer-events-none absolute right-2 top-2 rounded bg-black/70 px-2 py-0.5 text-xs">
                    Paused · tap to resume
                </span>
            )}
            {/* One row from ~296 px below 1024 px and ~245 px from 1024 px up; wraps below that rather than clip. */}
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 px-2 py-1 text-xs min-[1024px]:gap-x-1.5">
                <div className="flex items-center gap-1.5 min-[1024px]:gap-1">
                    <button type="button" aria-label="Slower" className={controlClass} onClick={() => setWpm((w) => Math.max(MIN_WPM, w - WPM_STEP))}>−</button>
                    <span className="whitespace-nowrap tabular-nums">{wpm} wpm</span>
                    <button type="button" aria-label="Faster" className={controlClass} onClick={() => setWpm((w) => Math.min(MAX_WPM, w + WPM_STEP))}>+</button>
                </div>
                <div className="flex items-center gap-1.5 min-[1024px]:gap-1" role="group" aria-label="Text size">
                    {(Object.keys(SIZES) as Size[]).map((s) => (
                        <button
                            key={s}
                            type="button"
                            aria-pressed={size === s}
                            // cn drops the base background, so the selected one can't lose on stylesheet order.
                            className={cn(controlClass, size === s && "bg-white/40 hover:bg-white/40")}
                            onClick={() => setSize(s)}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Teleprompter;
